import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import React from 'react';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';
import type { MessageDescriptor } from 'shared/i18n';
import UDCompactCheckbox from 'ud-form/components/compact-checkbox';
import ColorField from './color-field';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import MixedSelectField from './mixed-select-field';
import NumberField from './number-field';
import type { FillGradientControl, FillGradientDirection } from './use-fill-gradient';

const DIRECTION_OPTIONS: { value: FillGradientDirection; label: MessageDescriptor }[] = [
  { value: 'right', label: msg`Вправо →` },
  { value: 'left', label: msg`Влево ←` },
  { value: 'up', label: msg`Вверх ↑` },
  { value: 'down', label: msg`Вниз ↓` },
];

interface FillGradientSectionProps {
  control: FillGradientControl;
}

const toPercent = (fraction: number | undefined): number | undefined =>
  fraction === undefined ? undefined : Math.round(fraction * 100);

const clampPercent = (percent: number): number => Math.min(Math.max(percent, 0), 100) / 100;

interface StopFieldProps {
  color: FillGradientControl['color'];
  alpha: FillGradientControl['alpha'];
  onColorChange: (color: string) => void;
  onAlphaChange: (alpha: number) => void;
}

/**
 * One gradient stop: its color plus an independent opacity. Opacity is a separate
 * field because ColorField is an `<input type="color">`, which cannot carry alpha —
 * an `rgba()`/`#rrggbbaa` value would be silently rejected by its normalize().
 * Setting the end stop to 0% is what produces a vignette.
 */
const StopField: React.FC<StopFieldProps> = ({ color, alpha, onColorChange, onAlphaChange }) => {
  const { t } = useLingui();
  const MIXED = t`(разное)`;
  return (
    <Stop>
      <ColorField
        value={color.isMixed ? undefined : color.value}
        onChange={onColorChange}
        placeholder={color.isMixed ? MIXED : undefined}
      />
      <AlphaBox title={t`Непрозрачность, %`}>
        <NumberField
          value={alpha.isMixed ? undefined : toPercent(alpha.value)}
          onChange={(v) => onAlphaChange(clampPercent(v))}
          min={0}
          max={100}
          step={5}
          placeholder={alpha.isMixed ? MIXED : '%'}
        />
      </AlphaBox>
    </Stop>
  );
};

/**
 * Shared "Gradient" inspector section: a two-stop linear fill that holds the first
 * color up to an adjustable point and then transitions to the second toward one
 * side. Each stop carries its own opacity, so a vignette (fade-out) is just the end
 * stop at 0% — there is no separate mode, and toggling the section on seeds exactly
 * that. The visible gradient is materialized from `data.fillGradient` by
 * `editor.fillGradient` — this section only drives the config through
 * `useFillGradient`.
 */
const FillGradientSection: React.FC<FillGradientSectionProps> = ({ control }) => {
  const { t } = useLingui();
  const MIXED = t`(разное)`;
  const on = control.enabled.value === true;
  const directionOptions = DIRECTION_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));

  return (
    <InspectorSection
      title={t`Градиент`}
      headerAction={
        <GradientToggle checked={on} onChange={(e) => control.setEnabled(e.target.checked)} label={<Trans>Включить</Trans>} />
      }
    >
      {on && (
        <>
          <InspectorRow label={t`Цвет 1`}>
            <StopField
              color={control.color}
              alpha={control.alpha}
              onColorChange={(color) => control.patch({ color })}
              onAlphaChange={(alpha) => control.patch({ alpha })}
            />
          </InspectorRow>
          <InspectorRow label={t`Цвет 2`}>
            <StopField
              color={control.endColor}
              alpha={control.endAlpha}
              onColorChange={(endColor) => control.patch({ endColor })}
              onAlphaChange={(endAlpha) => control.patch({ endAlpha })}
            />
          </InspectorRow>
          <InspectorRow label={t`Направление`}>
            <MixedSelectField
              value={control.direction.value}
              isMixed={control.direction.isMixed}
              onChange={(v) => control.patch({ direction: v as FillGradientDirection })}
              options={directionOptions}
            />
          </InspectorRow>
          <InspectorRow label={t`Переход с`}>
            <NumberField
              value={control.fadeStart.isMixed ? undefined : toPercent(control.fadeStart.value)}
              onChange={(v) => control.patch({ fadeStart: clampPercent(v) })}
              min={0}
              max={100}
              step={5}
              emptyValue={50}
              placeholder={control.fadeStart.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        </>
      )}
    </InspectorSection>
  );
};

export default FillGradientSection;

const Stop = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const AlphaBox = styled.div`
  flex: 0 0 56px;
  min-width: 0;
`;

// Переключатель в шапке секции — мельче и акцентнее, чем подпись строки инспектора.
const GradientToggle = styled(UDCompactCheckbox)`
  font-size: 11px;
  font-weight: 600;
  color: ${editorAccent.ink};
`;
